import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Badge, Button, Modal, Text } from "@marekl/adminer/components";
import { ScanInput } from "../../components/ScanInput";
import {
  useRedeemCertificateItem,
  useUnredeemCertificateItem,
  type CertificateItemResponse,
  type CertificateResponse,
  type RedeemItemResponse,
} from "../../lib/queries/certificates";
import { ApiError } from "../../lib/api";
import { formatDateTime } from "@marekl/adminer/format";
import { classifyScan } from "../../lib/scan";
import { notifySuccess } from "@marekl/adminer/api";
import { useScanKeyboardCapture } from "../../lib/useScanKeyboardCapture";
import styles from "./CertificatesPage.module.css";

function itemStateBadge(state: string) {
  switch (state) {
    case "redeemed":
      return <Badge variant="ok">Směněno</Badge>;
    case "pending":
      return <Badge variant="warn">Čeká</Badge>;
    case "cancelled":
      return <Badge variant="err">Stornováno</Badge>;
    default:
      return <Badge>{state}</Badge>;
  }
}

interface RedeemItemRowProps {
  cert: CertificateResponse;
  item: CertificateItemResponse;
  highlighted: boolean;
  onRedeemed: (result: RedeemItemResponse) => void;
  onUnredeemed?: (item: CertificateItemResponse) => void;
}

export function RedeemItemRow({ cert, item, highlighted, onRedeemed, onUnredeemed }: RedeemItemRowProps) {
  const redeem = useRedeemCertificateItem(cert.id);
  const unredeem = useUnredeemCertificateItem(cert.id);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [unredeemConfirmOpen, setUnredeemConfirmOpen] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  // Capture bare digit keypresses into the modal's scan field while it is open.
  // withinModal: true skips the [aria-modal] guard that page-level hooks use.
  // When the modal is closed, scanRef.current is null (Modal renders nothing),
  // so this hook silently no-ops until the modal opens again.
  useScanKeyboardCapture(scanRef, (v) => {
    setScanValue(v);
    setScanError(null);
  }, { withinModal: true });

  useEffect(() => {
    if (scanModalOpen) {
      setScanValue("");
      setScanError(null);
      // Focus is handled by browser after portal mount; defer one tick.
      const t = setTimeout(() => scanRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [scanModalOpen]);

  async function performRedeem(voucherBarcode?: string) {
    setRedeemError(null);
    setScanError(null);
    try {
      const result = await redeem.mutateAsync({ itemId: item.id, voucherBarcode });
      const label = item.kind === "voucher" ? "Voucher vydán" : "Položka předána";
      notifySuccess(`${label}: ${item.product_name}`);
      setScanModalOpen(false);
      onRedeemed(result);
    } catch (err) {
      const message = err instanceof ApiError
        ? ((err.body as { message?: string } | null)?.message ?? "Výdej se nezdařil.")
        : "Výdej se nezdařil. Zkuste to znovu.";
      if (voucherBarcode !== undefined) {
        setScanError(message);
      } else {
        setRedeemError(message);
      }
    }
  }

  function handleRedeemClick() {
    if (item.kind === "voucher") {
      setScanModalOpen(true);
    } else {
      void performRedeem();
    }
  }

  function handleScanSubmit(raw: string) {
    const { normalized } = classifyScan(raw);
    if (normalized === "") {
      setScanError("Zadejte čárový kód voucheru.");
      return;
    }
    void performRedeem(normalized);
  }

  async function handleUnredeem() {
    setRedeemError(null);
    try {
      const result = await unredeem.mutateAsync({ itemId: item.id });
      notifySuccess(`Směna položky „${item.product_name}" byla vrácena.`);
      setUnredeemConfirmOpen(false);
      onUnredeemed?.(result.item);
    } catch (err) {
      const message = err instanceof ApiError
        ? ((err.body as { message?: string } | null)?.message ?? "Vrácení směny se nezdařilo.")
        : "Vrácení směny se nezdařilo. Zkuste to znovu.";
      setRedeemError(message);
      setUnredeemConfirmOpen(false);
    }
  }

  const isCertExpired = !!cert.valid_until && cert.valid_until.isBefore(dayjs());
  const canRedeem = cert.payment_state === "paid" && item.state === "pending" && !isCertExpired;
  const canUnredeem = item.state === "redeemed" && item.kind !== "voucher";

  return (
    <li className={`${styles.itemRow} ${highlighted ? styles.highlighted : ""}`}>
      <div className={styles.itemBarcode}>
        <Text variant="mono">{item.claim_barcode}</Text>
      </div>
      <div className={styles.itemInfo}>
        <div className={styles.itemName}>
          {item.product_name} {itemStateBadge(item.state)}
        </div>
        <div className={styles.itemMeta}>
          {item.state === "redeemed" && item.redeemed_at
            ? item.kind === "voucher" && item.redeemed_voucher_id
              ? <>Vydán voucher <Text variant="mono">{item.redeemed_voucher_id.slice(0, 8)}…</Text> · {formatDateTime(item.redeemed_at)}</>
              : <>Předáno zákazníkovi · {formatDateTime(item.redeemed_at)}</>
            : item.kind === "voucher"
              ? `Po stisknutí „Vydat voucher" naskenujete fyzický voucher typu „${item.product_name}", který předáváte zákazníkovi.`
              : "Nevoucherová položka – pouze předání zboží zákazníkovi."}
        </div>
        {redeemError && (
          <div className={styles.itemError}>{redeemError}</div>
        )}
      </div>
      <div className={styles.itemActions}>
        {item.state === "redeemed" && item.redeemed_voucher_id && (
          <Link to={`/vouchers/${item.redeemed_voucher_id}`} className={styles.detailLink}>
            <Button variant="default" size="sm">Detail voucheru</Button>
          </Link>
        )}
        {canRedeem && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleRedeemClick}
            disabled={redeem.isPending && item.kind !== "voucher"}
            loading={redeem.isPending && item.kind !== "voucher"}
          >
            {item.kind === "voucher" ? "Vydat voucher" : "Označit za předané"}
          </Button>
        )}
        {canUnredeem && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setUnredeemConfirmOpen(true)}
            disabled={unredeem.isPending}
            title="Vrátit směnu a označit položku jako čekající"
          >
            Vrátit směnu
          </Button>
        )}
      </div>

      {canUnredeem && (
        <Modal
          open={unredeemConfirmOpen}
          title="Vrátit směnu položky?"
          onClose={() => {
            if (!unredeem.isPending) setUnredeemConfirmOpen(false);
          }}
          width={480}
          footer={
            <>
              <Button
                variant="default"
                onClick={() => setUnredeemConfirmOpen(false)}
                disabled={unredeem.isPending}
              >
                Zrušit
              </Button>
              <Button
                variant="primary"
                onClick={handleUnredeem}
                disabled={unredeem.isPending}
                loading={unredeem.isPending}
              >
                Vrátit směnu
              </Button>
            </>
          }
        >
          <p>
            Opravdu chcete vrátit směnu položky{" „"}
            <strong>{item.product_name}</strong>{""}? Položka se vrátí do stavu
            „čeká" a bude ji možné znovu předat zákazníkovi.
          </p>
        </Modal>
      )}

      {item.kind === "voucher" && (
        <Modal
          open={scanModalOpen}
          title={`Vydat voucher: ${item.product_name}`}
          onClose={() => {
            if (!redeem.isPending) setScanModalOpen(false);
          }}
          width={520}
          footer={
            <>
              <Button
                variant="default"
                onClick={() => setScanModalOpen(false)}
                disabled={redeem.isPending}
              >
                Zrušit
              </Button>
              <Button
                variant="primary"
                onClick={() => handleScanSubmit(scanValue)}
                disabled={redeem.isPending || scanValue.trim() === ""}
                loading={redeem.isPending}
              >
                Vydat voucher
              </Button>
            </>
          }
        >
          <p>
            Naskenujte čárový kód <strong>fyzického voucheru</strong> typu
            {" „"}{item.product_name}{""}, který předáváte zákazníkovi. Kód
            z certifikátu (<Text variant="mono">{item.claim_barcode}</Text>) je
            pouze směnný — vlastní voucher má svůj vlastní EAN.
          </p>
          <ScanInput
            ref={scanRef}
            value={scanValue}
            onChange={setScanValue}
            onScan={handleScanSubmit}
            placeholder="Naskenujte EAN voucheru…"
            error={scanError ?? undefined}
            variant="compact"
            disabled={redeem.isPending}
          />
        </Modal>
      )}
    </li>
  );
}
